/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import { EditableText } from '../Editable.js';
import { inputStyle, monoLabel } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

export function WaitingOn() {
  const utils = trpc.useUtils();
  const list = trpc.actions.list.useQuery();
  const create = trpc.actions.create.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const complete = trpc.actions.complete.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const drop = trpc.actions.drop.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const update = trpc.actions.update.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });

  const [draftDesc, setDraftDesc] = useState('');
  const [draftOwner, setDraftOwner] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const description = draftDesc.trim();
    const ownerName = draftOwner.trim();
    if (!description || !ownerName) return;
    create.mutate(
      {
        description,
        estimatedMinutes: null,
        status: 'waiting',
        ownerName,
      },
      {
        onSuccess: () => {
          setDraftDesc('');
          setDraftOwner('');
        },
      },
    );
  };

  const items = (list.data ?? []).filter((a) => a.status === 'waiting');

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ ...monoLabel }}>Waiting on · delegated / pending replies</div>

      {list.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : items.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          No one's holding the ball.
        </p>
      ) : (
        <div>
          {items.map((a) => (
            <WaitingRow
              key={a.id}
              description={a.description}
              ownerName={a.ownerName ?? ''}
              onComplete={() => complete.mutate({ actionId: a.id })}
              onDrop={() => drop.mutate({ actionId: a.id })}
              onEditDescription={(next) => update.mutate({ actionId: a.id, description: next })}
              onEditOwner={(next) =>
                update.mutate({ actionId: a.id, ownerName: next === '' ? null : next })
              }
              pending={complete.isPending || drop.isPending || update.isPending}
            />
          ))}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}
      >
        <input
          type="text"
          value={draftOwner}
          onChange={(e) => setDraftOwner(e.target.value)}
          placeholder="Who"
          aria-label="Owner name"
          style={{ ...inputStyle, width: 140 }}
        />
        <input
          type="text"
          value={draftDesc}
          onChange={(e) => setDraftDesc(e.target.value)}
          placeholder="What you're waiting on"
          aria-label="Waiting-for description"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="submit"
          disabled={create.isPending || draftDesc.trim() === '' || draftOwner.trim() === ''}
          style={{
            padding: '10px 18px',
            background: p.fg,
            color: p.bg,
            border: 'none',
            fontSize: 13,
            cursor:
              create.isPending || draftDesc.trim() === '' || draftOwner.trim() === ''
                ? 'not-allowed'
                : 'pointer',
            opacity:
              create.isPending || draftDesc.trim() === '' || draftOwner.trim() === '' ? 0.6 : 1,
          }}
        >
          {create.isPending ? 'Adding…' : 'Add →'}
        </button>
      </form>
      {create.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{create.error.message}</span>
      ) : null}
    </section>
  );
}

function WaitingRow({
  description,
  ownerName,
  onComplete,
  onDrop,
  onEditDescription,
  onEditOwner,
  pending,
}: {
  description: string;
  ownerName: string;
  onComplete: () => void;
  onDrop: () => void;
  onEditDescription: (next: string) => void;
  onEditOwner: (next: string) => void;
  pending: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto auto 1fr auto',
        alignItems: 'center',
        gap: 12,
        padding: '12px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <button
        type="button"
        onClick={onComplete}
        disabled={pending}
        title="Mark resolved"
        aria-label="Mark resolved"
        style={{
          width: 18,
          height: 18,
          border: `1px solid ${p.line}`,
          background: 'transparent',
          cursor: pending ? 'wait' : 'pointer',
        }}
      />
      <EditableText
        value={ownerName}
        onCommit={(next) => {
          const trimmed = next.trim();
          if (trimmed === '' || trimmed === ownerName) return;
          onEditOwner(trimmed);
        }}
        ariaLabel="Edit owner"
        renderStyle={{
          ...monoLabel,
          color: p.fg,
          fontSize: 11,
          letterSpacing: '0.14em',
          paddingRight: 8,
        }}
        inputStyle={{ ...inputStyle, fontSize: 13, padding: '6px 8px', width: 140 }}
      />
      <EditableText
        value={description}
        onCommit={(next) => {
          const trimmed = next.trim();
          if (trimmed === '' || trimmed === description) return;
          onEditDescription(trimmed);
        }}
        ariaLabel="Edit waiting-for description"
        renderStyle={{ fontSize: 15, color: p.fg }}
        inputStyle={{ ...inputStyle, fontSize: 15, padding: '6px 8px' }}
      />
      <button
        type="button"
        onClick={onDrop}
        disabled={pending}
        title="Drop"
        aria-label="Drop"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 16,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
